import type { OwnLedgerCashFlow } from "./ownLedgerClient";
import { historyCurrentMonthKey, historyFactDateKey } from "./historyDateFilters";
import {
  compareOwnLedgerHistoryRowsForDisplay,
  historyDisplaySortTimestamp,
  matchesHistoryTypeFilter,
  type HistoryRelatedTxn,
} from "./historyRelated";

export type HistoryTypeFilter = "all" | "option" | "stock" | "cash";

export interface HistoryCashFlowRow extends HistoryRelatedTxn {
  instrument_type: "cash";
  cash_flow: true;
  cash_flow_category: string;
  cash_flow_direction: "in" | "out";
  /** Deleted by the user: left out of History and never counted. */
  cash_flow_deleted?: boolean;
  note?: string | null;
  source?: string | null;
  account_id?: string | null;
  account_label?: string | null;
  account_name?: string | null;
  account_short_name?: string | null;
  broker_id?: string | null;
  broker_name?: string | null;
  extensions?: Record<string, string>;
}

export function isCashFlowHistoryRow(
  txn: HistoryRelatedTxn,
): txn is HistoryCashFlowRow {
  return txn.instrument_type === "cash" || (txn as HistoryCashFlowRow).cash_flow === true;
}

const CATEGORY_LABELS: Record<string, string> = {
  deposit: "Deposit",
  withdrawal: "Withdrawal",
  tax: "Tax",
  margin_interest: "Margin interest",
  stock_lending_interest: "Stock lending",
  transfer_in: "Transfer in",
  transfer_out: "Transfer out",
  adjustment: "Adjustment",
};

const INFLOW_EDIT_CATEGORIES = [
  "deposit",
  "transfer_in",
  "adjustment",
  "stock_lending_interest",
] as const;
const OUTFLOW_EDIT_CATEGORIES = [
  "withdrawal",
  "tax",
  "transfer_out",
  "adjustment",
  "margin_interest",
] as const;

export function cashFlowEditableCategories(
  direction: "in" | "out",
): readonly string[] {
  return direction === "in" ? INFLOW_EDIT_CATEGORIES : OUTFLOW_EDIT_CATEGORIES;
}

/** A cash row the user deleted — kept in the ledger, excluded from every total. */
export function isDeletedCashFlowRow(txn: HistoryRelatedTxn): boolean {
  return isCashFlowHistoryRow(txn) && txn.cash_flow_deleted === true;
}

/** Cash flow DB id from a History(L) row (negative txn id). */
export function cashFlowIdFromHistoryRow(txn: HistoryRelatedTxn): number | null {
  if (!isCashFlowHistoryRow(txn)) return null;
  const id = Number(txn.id) || 0;
  return id < 0 ? Math.abs(id) : null;
}

export function cashFlowCategoryLabel(category: string | null | undefined): string {
  const key = String(category ?? "").trim().toLowerCase();
  return CATEGORY_LABELS[key] ?? (key ? key.replace(/_/g, " ") : "Cash");
}

export function cashFlowActionLabel(category: string, direction: string): string {
  const cat = String(category ?? "").trim().toLowerCase();
  if (cat === "deposit") return "DEP";
  if (cat === "withdrawal") return "WDL";
  if (cat === "tax") return "TAX";
  if (cat === "margin_interest") return "MARG";
  if (cat === "stock_lending_interest") return "LEND";
  if (cat === "transfer_in") return "XFER+";
  if (cat === "transfer_out") return "XFER-";
  if (cat === "adjustment") return direction === "in" ? "ADJ+" : "ADJ-";
  return direction === "in" ? "IN" : "OUT";
}

/** Default intraday time for cash rows without broker fill timestamps (US/Eastern). */
export function cashFlowDisplayTimestamp(
  flowDate: string | null | undefined,
  category: string | null | undefined,
): string {
  const date = String(flowDate ?? "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return "";
  const cat = String(category ?? "").trim().toLowerCase();
  const hourEt =
    cat === "margin_interest" || cat === "stock_lending_interest"
      ? 17
      : cat === "deposit" || cat === "withdrawal" || cat === "tax"
        ? 9
        : 12;
  return `${date}T${String(hourEt).padStart(2, "0")}:00:00.000-05:00`;
}

export function historyTransactionNotes(txn: HistoryRelatedTxn): string {
  if (isCashFlowHistoryRow(txn)) {
    const note = String(txn.note ?? "").trim();
    if (note) return note;
    return cashFlowCategoryLabel(txn.cash_flow_category);
  }
  const transferNote = String(txn.extensions?.transfer_note ?? "").trim();
  if (transferNote) return transferNote;
  const preview = String(txn.notes_preview ?? "").trim();
  if (preview) return preview;
  const body = txn.notes?.map((row) => String(row?.body ?? "").trim()).find(Boolean);
  return body ?? "";
}

function resolveFactAsOf(at?: Date): Date {
  // Array#filter passes (value, index) — never treat a bare number as the as-of date.
  return at instanceof Date && !Number.isNaN(at.getTime()) ? at : new Date();
}

export function isFactCashFlow(flow: OwnLedgerCashFlow, at?: Date): boolean {
  const when = resolveFactAsOf(at);
  const flowDate = String(flow.flow_date ?? "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(flowDate)) return false;
  if (flowDate > historyFactDateKey(when)) return false;
  const brokerRef = String(flow.broker_ref ?? "").trim();
  if (/^\d{4}-\d{2}$/.test(brokerRef) && brokerRef > historyCurrentMonthKey(when)) return false;
  if (String(flow.category ?? "").trim().toLowerCase() === "margin_interest") {
    const month = /^\d{4}-\d{2}$/.test(brokerRef) ? brokerRef : flowDate.slice(0, 7);
    if (month >= historyCurrentMonthKey(when)) return false;
  }
  return true;
}

export function isFactHistoryRow(txn: HistoryRelatedTxn, at?: Date): boolean {
  if (!isCashFlowHistoryRow(txn)) return true;
  const when = resolveFactAsOf(at);
  const tradeDate = String(txn.trade_date ?? "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(tradeDate)) return false;
  if (tradeDate > historyFactDateKey(when)) return false;
  const brokerRef = String(txn.extensions?.broker_ref ?? "").trim();
  if (/^\d{4}-\d{2}$/.test(brokerRef) && brokerRef > historyCurrentMonthKey(when)) return false;
  if (String(txn.cash_flow_category ?? "").trim().toLowerCase() === "margin_interest") {
    const month = /^\d{4}-\d{2}$/.test(brokerRef) ? brokerRef : tradeDate.slice(0, 7);
    if (month >= historyCurrentMonthKey(when)) return false;
  }
  return true;
}

export function cashFlowToHistoryRow(flow: OwnLedgerCashFlow): HistoryCashFlowRow {
  const flowId = Number(flow.id) || 0;
  const category = String(flow.category ?? "").trim().toLowerCase();
  const direction = flow.direction === "out" ? "out" : "in";
  const amount = Math.abs(Number(flow.amount) || 0);
  const broker = String(flow.broker_id ?? flow.broker ?? "").trim().toLowerCase();
  const flowDate = String(flow.flow_date ?? "").slice(0, 10);

  return {
    id: flowId > 0 ? -flowId : flowId,
    instrument_type: "cash",
    cash_flow: true,
    cash_flow_category: category,
    cash_flow_direction: direction,
    cash_flow_deleted: flow.ignored === true,
    action: category || (direction === "in" ? "deposit" : "withdrawal"),
    ticker: "",
    trade_date: flowDate,
    trade_timestamp: cashFlowDisplayTimestamp(flowDate, category),
    quantity: 0,
    avg_price: amount,
    note: flow.note ?? null,
    source: flow.source ?? null,
    account_id: flow.account_id ?? null,
    account_label: flow.account_label ?? flow.account_name ?? null,
    account_name: flow.account_name ?? flow.account_label ?? null,
    account_short_name: flow.account_short_name ?? null,
    broker_id: flow.broker_id ?? (broker || null),
    broker_name: flow.broker_name ?? null,
    extensions: {
      ...(broker ? { broker } : {}),
      cash_flow_category: category,
      ...(flow.broker_ref ? { broker_ref: String(flow.broker_ref).trim() } : {}),
    },
  };
}

export function matchesHistoryTypeFilterExtended(
  txn: HistoryRelatedTxn,
  type: HistoryTypeFilter,
): boolean {
  if (type === "all") return true;
  return matchesHistoryTypeFilter(txn, type);
}

export function matchesHistoryTickerFilter(txn: HistoryRelatedTxn, ticker: string): boolean {
  const needle = ticker.trim().toUpperCase();
  if (!needle) return true;
  if (isCashFlowHistoryRow(txn)) {
    const haystack = [
      historyTransactionNotes(txn),
      cashFlowCategoryLabel(txn.cash_flow_category),
      cashFlowActionLabel(txn.cash_flow_category, txn.cash_flow_direction),
      txn.extensions?.broker,
    ]
      .filter(Boolean)
      .join(" ")
      .toUpperCase();
    return haystack.includes(needle);
  }
  const haystack = [txn.ticker, historyTransactionNotes(txn)].filter(Boolean).join(" ").toUpperCase();
  return haystack.includes(needle);
}

function readMixedDisplayTimestamp(
  txn: HistoryRelatedTxn,
  ctx?: {
    byId?: Map<number, HistoryRelatedTxn>;
    all?: readonly HistoryRelatedTxn[];
  },
): string {
  if (isCashFlowHistoryRow(txn)) {
    const direct = String(txn.trade_timestamp ?? "").trim();
    if (direct) return direct;
    return cashFlowDisplayTimestamp(txn.trade_date, txn.cash_flow_category);
  }
  return historyDisplaySortTimestamp(txn, ctx);
}

/** Sort trades + cash flows for History(L) newest-first, interleaved by date/time. */
export function compareOwnLedgerHistoryDisplayRows(
  a: HistoryRelatedTxn,
  b: HistoryRelatedTxn,
  ctx?: {
    byId?: Map<number, HistoryRelatedTxn>;
    all?: readonly HistoryRelatedTxn[];
  },
): number {
  const cashA = isCashFlowHistoryRow(a);
  const cashB = isCashFlowHistoryRow(b);
  if (!cashA && !cashB) {
    return compareOwnLedgerHistoryRowsForDisplay(a, b, "desc", ctx);
  }

  const dateCmp = String(a.trade_date ?? "").localeCompare(String(b.trade_date ?? ""));
  if (dateCmp !== 0) return -dateCmp;

  const tsA = readMixedDisplayTimestamp(a, ctx);
  const tsB = readMixedDisplayTimestamp(b, ctx);
  const tsCmp = tsA.localeCompare(tsB);
  if (tsCmp !== 0) return -tsCmp;

  return (Number(b.id) || 0) - (Number(a.id) || 0);
}

export function sortOwnLedgerHistoryDisplayRows(
  rows: readonly HistoryRelatedTxn[],
): HistoryRelatedTxn[] {
  const byId = new Map<number, HistoryRelatedTxn>();
  for (const row of rows) {
    const id = Number(row.id);
    if (id) byId.set(id, row);
  }
  return [...rows].sort((a, b) =>
    compareOwnLedgerHistoryDisplayRows(a, b, { byId, all: rows }),
  );
}

export interface CashBalanceSnapshot {
  before: number;
  after: number;
}

/**
 * Running external-cash total before and after each cash row.
 *
 * This tracks money entering and leaving the account — deposits, withdrawals,
 * transfers and interest — not cash on hand: premiums, assignment proceeds and
 * commissions are transactions, not cash flows, so they never reach this table.
 * Forecast rows are skipped because they describe money that has not moved, and
 * deleted rows because the user has said they never happened.
 */
export function buildCashBalanceSnapshots(
  rows: readonly HistoryRelatedTxn[],
  at?: Date,
): Map<number, CashBalanceSnapshot> {
  const ordered = rows
    .filter(
      (row): row is HistoryCashFlowRow =>
        isCashFlowHistoryRow(row) &&
        !isDeletedCashFlowRow(row) &&
        String(row.source ?? "").trim() !== "forecast_tax" &&
        isFactHistoryRow(row, at),
    )
    .sort((a, b) => {
      const dateCmp = String(a.trade_date ?? "").localeCompare(String(b.trade_date ?? ""));
      if (dateCmp !== 0) return dateCmp;
      const tsCmp = readMixedDisplayTimestamp(a).localeCompare(readMixedDisplayTimestamp(b));
      if (tsCmp !== 0) return tsCmp;
      return (Number(a.id) || 0) - (Number(b.id) || 0);
    });

  const snapshots = new Map<number, CashBalanceSnapshot>();
  let balance = 0;
  for (const row of ordered) {
    const amount = Math.abs(Number(row.avg_price) || 0);
    const before = balance;
    balance += row.cash_flow_direction === "out" ? -amount : amount;
    const id = Number(row.id) || 0;
    if (id) snapshots.set(id, { before, after: balance });
  }
  return snapshots;
}

export async function loadOwnLedgerHistoryDisplayRows(
  loadTrades: () => Promise<HistoryRelatedTxn[]>,
  loadCashFlows: () => Promise<OwnLedgerCashFlow[]>,
): Promise<HistoryRelatedTxn[]> {
  const [trades, flows] = await Promise.all([
    loadTrades(),
    loadCashFlows().catch(() => [] as OwnLedgerCashFlow[]),
  ]);
  const cashRows = flows.filter((flow) => isFactCashFlow(flow)).map(cashFlowToHistoryRow);
  return sortOwnLedgerHistoryDisplayRows([...trades, ...cashRows]);
}
